import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Avatar,
  AvatarFallback,
  AvatarImage,
  getUserInitials,
  getUserAvatarUrl
} from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import type { DashboardCoWorker } from '@bliks-io/contracts';

interface DashboardCoWorkersProps {
  coWorkers: DashboardCoWorker[];
}

const RELATIONSHIP_COLORS: Record<string, string> = {
  direct: 'bg-positive-surface-strong text-positive-strong',
  upstream: 'bg-info-surface-strong text-info-strong',
  downstream: 'bg-warning-surface-strong text-warning-strong'
};

function CoWorkerCard({ coWorker, slug }: { coWorker: DashboardCoWorker; slug: string }) {
  const { t } = useTranslation();

  return (
    <Link
      to="/$slug/org-members/$orgMemberId"
      params={{ slug, orgMemberId: coWorker._id }}
      className="flex w-[160px] shrink-0 flex-col items-center gap-2 rounded-lg border p-3 transition-colors hover:bg-muted/50"
    >
      <Avatar>
        <AvatarImage
          src={getUserAvatarUrl({ avatar: coWorker.avatar ?? undefined }, 'thumbnail')}
          alt={`${coWorker.firstName} ${coWorker.lastName}`}
        />
        <AvatarFallback className="bg-yellow-avatar text-white">
          {getUserInitials(coWorker.firstName, coWorker.lastName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 text-center">
        <p className="text-sm font-medium">
          {coWorker.firstName} {coWorker.lastName}
        </p>
        {coWorker.role && <p className="text-xs text-muted-foreground">{coWorker.role.name}</p>}
      </div>
      <Badge
        variant="outline"
        className={`text-xs ${RELATIONSHIP_COLORS[coWorker.relationship] ?? ''}`}
      >
        {t(`dashboard.coWorkers.${coWorker.relationship}`)}
      </Badge>
    </Link>
  );
}

function CoWorkerList({
  coWorkers,
  emptyKey,
  slug
}: {
  coWorkers: DashboardCoWorker[];
  emptyKey: string;
  slug: string;
}) {
  const { t } = useTranslation();

  if (coWorkers.length === 0) {
    return (
      <div className="flex min-h-[140px] items-center justify-center">
        <p className="text-sm text-muted-foreground italic">{t(emptyKey)}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[140px] gap-3 overflow-x-auto pb-2">
      {coWorkers.map((cw) => (
        <CoWorkerCard key={cw._id} coWorker={cw} slug={slug} />
      ))}
    </div>
  );
}

export function DashboardCoWorkers({ coWorkers }: DashboardCoWorkersProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();

  const { direct, upstream, downstream } = useMemo(
    () => ({
      direct: coWorkers.filter((c) => c.relationship === 'direct'),
      upstream: coWorkers.filter((c) => c.relationship === 'upstream'),
      downstream: coWorkers.filter((c) => c.relationship === 'downstream')
    }),
    [coWorkers]
  );

  return (
    <Card>
      <Tabs defaultValue="all">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">{t('dashboard.coWorkers.title')}</CardTitle>
          <TabsList>
            <TabsTrigger value="all">
              {t('dashboard.coWorkers.all')} ({coWorkers.length})
            </TabsTrigger>
            <TabsTrigger value="direct">
              {t('dashboard.coWorkers.direct')} ({direct.length})
            </TabsTrigger>
            <TabsTrigger value="upstream">
              {t('dashboard.coWorkers.upstream')} ({upstream.length})
            </TabsTrigger>
            <TabsTrigger value="downstream">
              {t('dashboard.coWorkers.downstream')} ({downstream.length})
            </TabsTrigger>
          </TabsList>
        </CardHeader>
        <CardContent>
          <TabsContent value="all" className="mt-0">
            <CoWorkerList
              coWorkers={coWorkers}
              emptyKey="dashboard.coWorkers.noCoWorkers"
              slug={slug}
            />
          </TabsContent>
          <TabsContent value="direct" className="mt-0">
            <CoWorkerList
              coWorkers={direct}
              emptyKey="dashboard.coWorkers.noCoWorkersDirect"
              slug={slug}
            />
          </TabsContent>
          <TabsContent value="upstream" className="mt-0">
            <CoWorkerList
              coWorkers={upstream}
              emptyKey="dashboard.coWorkers.noCoWorkersUpstream"
              slug={slug}
            />
          </TabsContent>
          <TabsContent value="downstream" className="mt-0">
            <CoWorkerList
              coWorkers={downstream}
              emptyKey="dashboard.coWorkers.noCoWorkersDownstream"
              slug={slug}
            />
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
}
